import React from 'react';
import { useAppSelector } from '../store';
import { selectTotalUsedCargoTonnage } from '../store/selectors/derivedSelectors';

export const TonnageDebugger: React.FC = () => {
  const state = useAppSelector((state) => state);
  const totalUsedTonnage = useAppSelector(selectTotalUsedCargoTonnage);
  
  // Extract individual components for debugging
  const supplyTons = state.shipBuilder.supplyTons || 0;
  const ammunitionStorage = state.shipBuilder.ammunitionStorage || [];
  const optionalSystems = state.optionalSystems;
  const weaponMounts = state.shipBuilder.weaponMounts || [];
  const defensiveSystems = state.shipBuilder.defensiveSystems || [];
  const stateroomConfig = state.shipBuilder.stateroomConfig;
  const fuelTanks = state.shipBuilder.fuelTanks || 0;
  const hullAndArmorStats = state.shipBuilder.hullAndArmorStats;
  const bridge = state.shipBuilder.bridge;
  const sensors = state.shipBuilder.sensors;
  const powerPlant = state.shipBuilder.powerPlant;

  // Calculate each component individually
  const systemsTonnage = [
    ...optionalSystems.structure,
    ...optionalSystems.modularHull,
    ...optionalSystems.power,
    ...optionalSystems.drives,
    ...optionalSystems.fuel,
    ...optionalSystems.accommodations,
    ...optionalSystems.bridge,
    ...optionalSystems.cargo.filter((system: any) => system.subtype !== "externalCargoMount"),
    ...optionalSystems.drones,
    ...optionalSystems.sensors,
    ...optionalSystems.external,
    ...optionalSystems.internal,
    ...optionalSystems.software,
    ...optionalSystems.commonAreas,
  ].reduce((total: number, system: any) => total + (system.tonnage || 0), 0);

  // Calculate weapon mounts tonnage using correct Traveller rules (mount.tons includes mount base + weapon weight)
  const weaponMountsTonnage = weaponMounts.reduce((total: number, mount: any) => {
    return total + (mount.tons || 0);
  }, 0);

  // Calculate defensive systems tonnage using correct Traveller rules (multiply by quantity)
  const defensiveSystemsTonnage = defensiveSystems.reduce((total: number, system: any) => {
    const quantity = system.quantity || 1;
    return total + ((system.weight || 0) * quantity);
  }, 0);

  // Calculate stateroom tonnage the same way as the footer (including common area)
  let stateroomTonnage = 0;
  if (stateroomConfig) {
    stateroomTonnage += (stateroomConfig.single || 0) * 4; // Standard staterooms
    stateroomTonnage += (stateroomConfig.double || 0) * 4; // Double staterooms (same as single)
    if (stateroomConfig.lowBerths) {
      stateroomTonnage += (stateroomConfig.lowBerths.count || 0) * 0.5;
    }
    if (stateroomConfig.emergencyLowBerths) {
      stateroomTonnage += (stateroomConfig.emergencyLowBerths.count || 0) * 1;
    }
    // Add common area tonnage (this might be the missing piece)
    stateroomTonnage += (stateroomConfig.commonArea || 0);
  }

  const ammoTons = ammunitionStorage.reduce((total: number, ammo: any) => {
    const quantity = ammo.quantity || 0;
    return total + ((ammo.tonsPerReload || 0) * quantity);
  }, 0);

  const hullAndArmorTonnage = hullAndArmorStats?.totalConsumedVolume || 0;
  const bridgeTonnage = bridge?.weight || 0;
  const sensorsTonnage = sensors?.tons || 0;
  // Get driveStats from the state
  const driveStats = state.shipBuilder.driveStats;
  const drivesTonnage = driveStats?.totalConsumedVolume || 0;
  const powerPlantTonnage = powerPlant?.weight || 0;

  const manualTotal = systemsTonnage + 
                     weaponMountsTonnage + 
                     defensiveSystemsTonnage + 
                     stateroomTonnage + 
                     ammoTons + 
                     supplyTons + 
                     fuelTanks + 
                     hullAndArmorTonnage + 
                     bridgeTonnage + 
                     sensorsTonnage + 
                     drivesTonnage + 
                     powerPlantTonnage;

  return (
    <div className="p-5 bg-gray-700 m-5 rounded-lg text-white">
      <h3 className="text-xl font-bold mb-4 text-white">Tonnage Debug Breakdown</h3>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><strong>Component</strong></div>
        <div><strong>Tons</strong></div>
        
        <div>Optional Systems:</div>
        <div>{systemsTonnage.toFixed(1)}</div>
        
        <div>Weapon Mounts:</div>
        <div>{weaponMountsTonnage.toFixed(1)}</div>
        
        <div>Defensive Systems:</div>
        <div>{defensiveSystemsTonnage.toFixed(1)}</div>
        
        <div>Staterooms:</div>
        <div>{stateroomTonnage.toFixed(1)}</div>
        
        <div>Ammunition Storage:</div>
        <div>{ammoTons.toFixed(1)}</div>
        
        <div>Supplies:</div>
        <div>{supplyTons.toFixed(1)}</div>
        
        <div>Fuel Tanks:</div>
        <div>{fuelTanks.toFixed(1)}</div>
        
        <div>Hull & Armor:</div>
        <div>{hullAndArmorTonnage.toFixed(1)}</div>
        
        <div>Bridge:</div>
        <div>{bridgeTonnage.toFixed(1)}</div>
        
        <div>Sensors:</div>
        <div>{sensorsTonnage.toFixed(1)}</div>
        
        <div>Drives:</div>
        <div>{drivesTonnage.toFixed(1)}</div>
        
        <div>Power Plant:</div>
        <div>{powerPlantTonnage.toFixed(1)}</div>
        
        <div className="border-t-2 border-gray-500 pt-2"><strong>Manual Total:</strong></div>
        <div className="border-t-2 border-gray-500 pt-2"><strong>{manualTotal.toFixed(1)}</strong></div>
        
        <div className="border-t-2 border-gray-500 pt-2"><strong>System Total:</strong></div>
        <div className="border-t-2 border-gray-500 pt-2"><strong>{totalUsedTonnage.toFixed(1)}</strong></div>
        
        <div className="border-t-2 border-gray-500 pt-2"><strong>Difference:</strong></div>
        <div className="border-t-2 border-gray-500 pt-2"><strong>{(totalUsedTonnage - manualTotal).toFixed(1)}</strong></div>
      </div>
      
      <div className="mt-5">
        <h4 className="text-lg font-semibold mb-3 text-white">Detailed Breakdown:</h4>
        <div className="text-xs text-gray-300 space-y-1">
          <div><strong>Optional Systems Details:</strong></div>
          <div>Structure: {optionalSystems.structure.reduce((sum: number, s: any) => sum + (s.tonnage || 0), 0).toFixed(1)} tons</div>
          <div>Accommodations: {optionalSystems.accommodations.reduce((sum: number, s: any) => sum + (s.tonnage || 0), 0).toFixed(1)} tons</div>
          <div>Internal: {optionalSystems.internal.reduce((sum: number, s: any) => sum + (s.tonnage || 0), 0).toFixed(1)} tons</div>
          <div>Other: {(systemsTonnage - optionalSystems.structure.reduce((sum: number, s: any) => sum + (s.tonnage || 0), 0) - optionalSystems.accommodations.reduce((sum: number, s: any) => sum + (s.tonnage || 0), 0) - optionalSystems.internal.reduce((sum: number, s: any) => sum + (s.tonnage || 0), 0)).toFixed(1)} tons</div>
        </div>
      </div>
    </div>
  );
};
